import React, { useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Row,
  Col,
  Card,
  Image,
  Button,
  Timeline,
  Divider,
  Modal,
  message,
  Tag,
  Typography,
  Space,
  Badge,
  Alert,
  Spin,
  Input,
  Tooltip,
} from "antd";
import { getDetail, getMyDetails, getUser, postQuote } from "../services/api";
import {
  UserOutlined,
  CalendarOutlined,
  EnvironmentOutlined,
  CaretUpFilled,
  CaretDownFilled,
  CarOutlined,
  SendOutlined,
  GoldFilled,
  ClockCircleOutlined,
  MoonOutlined,
  CheckCircleFilled,
  CloseCircleOutlined,
  CloseCircleFilled,
  CreditCardOutlined,
  GiftOutlined,
  TeamOutlined,
  HomeOutlined,
  CrownOutlined,
} from "@ant-design/icons";

import { useNavigate, useLocation } from "react-router-dom";
import Cookies from "js-cookie";
import { trackEvent } from "../analytics";
import { tagProps } from "../components/utils";
import "./ServiceDetails.css";
import { DownloadItineraryButton } from "../components/TripItineraryPDF";

const { Title, Paragraph, Text } = Typography;

export default function ServiceDetail() {
  const accessToken = Cookies.get("token");
  const { sku } = useParams();
  const queryClient = useQueryClient();
  const [modalOpen, setModalOpen] = useState(false);
  const [openCards, setOpenCards] = useState(true);
  const [loading, setLoading] = useState(null);
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("+91");
  const [emailLookupLoading, setEmailLookupLoading] = useState(false);
  const [emailLookupError, setEmailLookupError] = useState(null);

  const navigate = useNavigate();
  const location = useLocation();
  const [messageApi, contextHolder] = message.useMessage();
  const normalizedSKU = sku?.toUpperCase();
  let getuser = queryClient.getQueryData(["myDetails"]);
  const { data: user } = useQuery({
    queryKey: ["myDetails"],
    queryFn: async () => {
      const response = await getMyDetails(accessToken);
      return response.data;
    },
    enabled: !!accessToken && !getuser,
    refetchOnWindowFocus: false,
  });
  const userDetails = getuser ? getuser : user;
  const { data, isLoading } = useQuery({
    queryKey: [`detail-${normalizedSKU}`],
    queryFn: async () => {
      const response = await getDetail(normalizedSKU);
      return response;
    },
    enabled: !!normalizedSKU,
    staleTime: 1000 * 60 * 5,
  });

  const tag = tagProps[data?.status?.toLowerCase()];
  const handleEmailSearch = async () => {
    if (!email) return;

    try {
      setEmailLookupLoading(true);
      setEmailLookupError(null);

      const res = await getUser(accessToken, email);

      if (res) {
        setPhone(res.phoneNo || "");
      } else {
        setPhone("");
        setEmailLookupError("User not found");
      }
    } catch (err) {
      setPhone("");
      setEmailLookupError("User not found");
    } finally {
      setEmailLookupLoading(false);
    }
  };
  const quoteMutation = useMutation({
    mutationFn: (payload) => postQuote(payload, accessToken),
    onSuccess: (data) => {
      setLoading(false);
      messageApi.success("Quote request sent! We will contact you soon.");
      queryClient.invalidateQueries(["detail", normalizedSKU]);
      queryClient.invalidateQueries(["myDetails"]);
      setModalOpen(false);
    },
    onError: () => {
      setModalOpen(false);
      setLoading(false);
      messageApi.error(
        "Failed to send quote. Please try again.\n or contact support.",
      );
    },
  });
  const normalizePhone = (value) => {
    if (!value) return undefined;

    const trimmed = value.trim();

    if (trimmed === "+91" || trimmed.length <= 3) {
      return undefined;
    }

    return trimmed;
  };

  const handleConfirm = () => {
    const basePayload = {
      sku: data.SKU,
      title: data?.title,
      initiatedDate: new Date().toISOString().split("T")[0],
    };

    const payload =
      userDetails?.role === "admin"
        ? {
            ...basePayload,
            email: email || undefined,
            phone: normalizePhone(phone),
          }
        : basePayload;

    const category = data.SKU.split("-")[0].toLowerCase();

    trackEvent(`get_price_${category}_product`, {
      sku: data.SKU,
    });

    setLoading(true);
    quoteMutation.mutate(payload);
  };

  const photos = useMemo(
    () => (Array.isArray(data?.photos) ? data.photos : []),
    [data],
  );

  const renderBulletList = (title, arr) => {
    if (!Array.isArray(arr) || arr.length === 0) return null;

    return (
      <Card size="small" title={title} style={{ marginBottom: 16 }}>
        <ul
          style={{
            listStyle: "disc outside",
            paddingLeft: 24,
            marginTop: 8,
          }}
        >
          {arr.map((item, idx) => (
            <li
              key={idx}
              style={{
                display: "list-item",
                listStyle: "disc outside",
                marginBottom: 4,
              }}
            >
              <Text>{item}</Text>
            </li>
          ))}
        </ul>
      </Card>
    );
  };

  const renderPaymentOptions = (payment) => {
    if (!payment) return null;

    return (
      <Card size="small" title="Payment Options" style={{ marginBottom: 16 }}>
        <ul
          style={{
            listStyleType: "disc",
            paddingLeft: 24,
            margin: "8px 0",
          }}
        >
          {payment.full_payment && <li>Full payment available</li>}
          {payment.emi_available && (
            <li>No-cost EMI available ({payment.emi_tenure?.join(" & ")})</li>
          )}
          {Array.isArray(payment.payment_methods) && (
            <li>Payment methods: {payment.payment_methods.join(", ")}</li>
          )}
        </ul>
        {payment.emi_note && (
          <Text type="secondary" style={{ fontSize: 12 }}>
            {payment.emi_note}
          </Text>
        )}
      </Card>
    );
  };
  const getPromotionIcon = (title = "") => {
    const name = title.toLowerCase();

    if (name.includes("emi")) return <CreditCardOutlined />;
    if (name.includes("early")) return <GiftOutlined />;
    if (name.includes("pay") || name.includes("come")) {
      return <TeamOutlined />;
    }

    return <GoldFilled />;
  };

  const renderPromotions = (promotions) => {
    const activePromotions = Array.isArray(promotions)
      ? promotions.filter((item) => item.enabled === true)
      : [];

    if (activePromotions.length === 0) return null;

    return (
      <div className="service-promotions">
        <div className="service-promotions-heading">
          <div>
            <div className="service-promotions-title">Special Offers</div>
            <div className="service-promotions-subtitle">
              Exclusive deals available for this trip
            </div>
          </div>

          <Tag color="gold">LIMITED OFFERS</Tag>
        </div>

        <Row gutter={[12, 12]}>
          {activePromotions.map((item, index) => (
            <Col xs={24} sm={12} lg={8} key={`${item.title}-${index}`}>
              <div className="service-promotion-card">
                <div className="service-promotion-icon">
                  {getPromotionIcon(item.title)}
                </div>

                <div className="service-promotion-content">
                  <div className="service-promotion-name">{item.title}</div>

                  <div className="service-promotion-description">
                    {item.description}
                  </div>
                </div>
              </div>
            </Col>
          ))}
        </Row>
      </div>
    );
  };
  const renderTags = (tags) => {
    if (!Array.isArray(tags) || tags.length === 0) return null;
    return (
      <Card size="small" title="Tags" style={{ marginBottom: 16 }}>
        <Space wrap>
          {tags.map((tag, idx) => (
            <Tag key={idx} color="blue">
              {tag}
            </Tag>
          ))}
        </Space>
      </Card>
    );
  };

  const handleActionClick = (action) => {
    if (!accessToken) {
      const redirectTo = encodeURIComponent(
        location.pathname + location.search,
      );
      navigate(`/?auth=signin&redirect=${redirectTo}`);
      return;
    }

    setModalOpen(true);
  };

  const renderItinerary = (itinerary, transfers = []) => {
    if (!Array.isArray(itinerary) || itinerary.length === 0) return null;

    const meals = [
      { key: "breakfast", label: "Breakfast", icon: "☕" },
      { key: "lunch", label: "Lunch", icon: "🍽️" },
      { key: "dinner", label: "Dinner", icon: "🌙" },
    ];

    const transportConfig = {
      road: {
        label: "ROAD TRANSFER",
        icon: <CarOutlined />,
      },
      flight: {
        label: "FLIGHT TRANSFER",
        icon: <SendOutlined />,
      },
      ship: {
        label: "SHIP TRANSFER",
        icon: <GoldFilled />,
      },
      boat: {
        label: "BOAT TRANSFER",
        icon: <GoldFilled />,
      },
    };

    // Normalize meal values
    const getMealInfo = (meal) => {
      if (typeof meal === "object" && meal !== null) {
        return {
          included: meal.included === true,
          location: meal.location || null,
        };
      }

      return {
        included: meal === true,
        location: null,
      };
    };

    // Render meal icons in the day heading
    const renderMealIcons = (step) => {
      const availableMeals = meals.filter((meal) =>
        Object.prototype.hasOwnProperty.call(step.meals || {}, meal.key),
      );

      if (availableMeals.length === 0) return null;

      return (
        <div className="journey-meal-list">
          {availableMeals.map((meal) => {
            const mealInfo = getMealInfo(step.meals[meal.key]);

            return (
              <Tag
                style={{ width: "70px", textAlign: "center" }}
                key={meal.key}
                color={mealInfo.included ? "success" : "default"}
                className="journey-meal-tag"
              >
                {meal.label} ·{" "}
                {mealInfo.included ? (
                  <CheckCircleFilled className="journey-meal-check" />
                ) : (
                  <CloseCircleOutlined className="journey-meal-cross" />
                )}
              </Tag>
            );
          })}
        </div>
      );
    };

    // Render transfer card
    const renderTransfer = (transfer, key) => {
      const type = transfer.type?.toLowerCase();
      const config = transportConfig[type] || {
        label: "TRANSFER",
        icon: <CarOutlined />,
      };

      return (
        <div className="journey-transfer" key={key}>
          <div className="journey-transfer-icon">{config.icon}</div>

          <div className="journey-transfer-content">
            <div className="journey-transfer-header">
              <span className="journey-transfer-label">{config.label}</span>

              <Tag color={transfer.included ? "success" : "default"}>
                {transfer.included ? "Included" : "Not Included"}
              </Tag>
            </div>

            <div className="journey-transfer-route">
              {transfer.from || "Starting point"}
              <span> → </span>
              {transfer.to || "Destination"}
            </div>

            {(transfer.duration || transfer.note) && (
              <div className="journey-transfer-meta">
                {transfer.duration && (
                  <span>
                    <ClockCircleOutlined /> {transfer.duration}
                  </span>
                )}

                {transfer.note && <span>{transfer.note}</span>}
              </div>
            )}
          </div>
        </div>
      );
    };

    return (
      <Card
        className="journey-main-card"
        title={
          <div className="journey-main-heading">
            <div className="journey-heading-text">
              <div className="journey-main-title">Your Journey</div>
              <div className="journey-main-subtitle">
                Explore your trip day by day
              </div>
            </div>
          </div>
        }
        extra={
          <div className="journey-header-actions">
            <DownloadItineraryButton data={data} type="card" />

            <Button
              size="small"
              icon={openCards ? <CaretUpFilled /> : <CaretDownFilled />}
              onClick={() => setOpenCards((prev) => !prev)}
            >
              {openCards ? "Collapse" : "Expand"}
            </Button>

            <Tag color="blue">{itinerary.length} DAYS</Tag>
          </div>
        }
      >
        {openCards && (
          <>
            {/* Route summary */}
            {data?.route && (
              <div className="journey-route-summary">
                <EnvironmentOutlined />
                <span>{data.route}</span>
              </div>
            )}

            {/* Journey timeline */}
            <div className="journey-days">
              {itinerary.map((step, index) => {
                const dayNumber = String(index + 1).padStart(2, "0");

                const dayTransfers = transfers.filter(
                  (transfer) => transfer.afterDay === step.day,
                );

                return (
                  <React.Fragment key={step.day || index}>
                    {/* Day card */}
                    <div className="journey-flow-item">
                      <div className="journey-flow-marker">
                        <div className="journey-day-number">{dayNumber}</div>
                      </div>

                      <div className="journey-day-card">
                        {/* Day heading */}
                        <div className="journey-day-header">
                          <div className="journey-day-heading-content">
                            <div className="journey-day-label">
                              {step.day || `Day ${index + 1}`}
                            </div>

                            <div className="journey-day-title">
                              {step.title}
                            </div>

                            {step.subtitle && (
                              <div className="journey-day-subtitle">
                                {step.subtitle}
                              </div>
                            )}
                          </div>

                          {/* Meal icons on right */}
                          {step.meals && renderMealIcons(step)}
                        </div>

                        {/* Image */}
                        {step.image && (
                          <Image
                            src={step.image}
                            alt={step.title || "Journey image"}
                            preview={false}
                            width="100%"
                            style={{
                              width: "100%",
                              height: "auto",
                              maxHeight: 350,
                              objectFit: "contain",
                              borderRadius: 10,
                              marginTop: 14,
                              display: "block",
                            }}
                          />
                        )}

                        {/* Activities */}
                        <div className="journey-activities">
                          {Array.isArray(step.details) ? (
                            <ul>
                              {step.details.map((detail, i) => (
                                <li
                                  key={i}
                                  style={{
                                    display: "list-item",
                                    listStyle: "disc outside",
                                    marginBottom: 4,
                                  }}
                                >
                                  {detail}
                                </li>
                              ))}
                            </ul>
                          ) : (
                            <Paragraph>{step.details}</Paragraph>
                          )}
                        </div>

                        {/* Overnight */}
                        {step.overnight && (
                          <div className="journey-overnight">
                            <MoonOutlined />
                            <span>
                              Overnight stay
                              <strong> · {step.overnight}</strong>
                            </span>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Transfers after this day */}
                    {dayTransfers.map((transfer, transferIndex) => {
                      const type = transfer.type?.toLowerCase();
                      const config = transportConfig[type] || {
                        icon: <CarOutlined />,
                      };

                      return (
                        <div
                          className="journey-flow-item journey-flow-transfer"
                          key={`transfer-${index}-${transferIndex}`}
                        >
                          <div className="journey-flow-marker">
                            <div className="journey-transfer-node">
                              {config.icon}
                            </div>
                          </div>

                          <div className="journey-transfer-wrapper">
                            {renderTransfer(
                              transfer,
                              `transfer-card-${index}-${transferIndex}`,
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </React.Fragment>
                );
              })}
            </div>

            {/* Legend */}
            <div className="journey-legend">
              <span>
                <CheckCircleFilled className="journey-meal-check" />
                Included in package
              </span>

              <span>
                <CloseCircleOutlined className="journey-meal-cross" />
                Not included
              </span>
            </div>
          </>
        )}
      </Card>
    );
  };
  const renderStayOptions = (options) => {
    const activeOptions = Array.isArray(options)
      ? options.filter((item) => item.enabled === true)
      : [];

    if (!activeOptions.length) return null;

    return (
      <div className="service-promotions">
        <div className="service-promotions-heading">
          <div>
            <div className="service-promotions-title">Stay Options</div>
            <div className="service-promotions-subtitle">
              Choose from available accommodation options
            </div>
          </div>
        </div>

        <Row gutter={[12, 12]}>
          {activeOptions.map((item, index) => (
            <Col xs={24} sm={12} lg={8} key={`${item.name}-${index}`}>
              <div className="service-promotion-card service-option-card">
                <div className="service-promotion-icon service-option-icon">
                  <HomeOutlined />
                </div>

                <div className="service-promotion-content">
                  <div className="service-promotion-name">{item.name}</div>

                  <div className="service-promotion-description">
                    {item.price == null || item.price_type === "ask_price"
                      ? "Price on request"
                      : `${data?.currency || "₹"} ${Number(
                          item.price,
                        ).toLocaleString("en-IN")} ${
                          {
                            per_person: "/ person",
                            per_couple: "/ couple",
                            per_group: "/ group",
                            fixed: "",
                          }[item.price_type] || ""
                        }`}
                  </div>
                </div>
              </div>
            </Col>
          ))}
        </Row>
      </div>
    );
  };
  const renderAddons = (options) => {
    const activeOptions = Array.isArray(options)
      ? options.filter((item) => item.enabled === true)
      : [];

    if (!activeOptions.length) return null;

    const categories = [
      { key: "activity", label: "Activities", icon: <CrownOutlined /> },
      { key: "stay", label: "Stay Upgrades", icon: <HomeOutlined /> },
      { key: "transport", label: "Transport", icon: <CarOutlined /> },
    ];

    return (
      <div className="service-promotions">
        <div className="service-promotions-heading">
          <div>
            <div className="service-promotions-title">Optional Add-ons</div>
            <div className="service-promotions-subtitle">
              Additional experiences and services available for your trip
            </div>
          </div>
        </div>

        {categories.map((category) => {
          const items = activeOptions.filter(
            (item) => item.category?.toLowerCase() === category.key,
          );

          if (!items.length) return null;

          return (
            <div key={category.key} style={{ marginBottom: 14 }}>
              <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 10 }}>
                {category.icon} {category.label}
              </div>

              <Row gutter={[12, 12]}>
                {items.map((item, index) => (
                  <Col xs={24} sm={12} lg={8} key={`${item.name}-${index}`}>
                    <div className="service-promotion-card service-option-card">
                      <div className="service-promotion-icon service-option-icon">
                        {category.icon}
                      </div>

                      <div className="service-promotion-content">
                        <div className="service-promotion-name">
                          {item.name}
                        </div>

                        <div className="service-promotion-description">
                          {item.price == null || item.price_type === "ask_price"
                            ? "Price on request"
                            : `${data?.currency || "₹"} ${Number(
                                item.price,
                              ).toLocaleString("en-IN")} ${
                                {
                                  per_person: "/ person",
                                  per_couple: "/ couple",
                                  per_group: "/ group",
                                  fixed: "",
                                }[item.price_type] || ""
                              }`}
                        </div>
                      </div>
                    </div>
                  </Col>
                ))}
              </Row>
            </div>
          );
        })}
      </div>
    );
  };
  return (
    <>
      {contextHolder}
      {!isLoading && data ? (
        <div className="service-detail-page">
          <Row className="service-detail-row" gutter={[24, 24]}>
            <Col xs={24} md={10} lg={8} className="service-detail-left">
              <Card variant="borderless" styles={{ body: { padding: 8 } }}>
                {photos.length > 0 ? (
                  <div>
                    <Image.PreviewGroup>
                      <div style={{ textAlign: "center" }}>
                        <Image
                          src={photos[0]}
                          style={{
                            maxHeight: 200,
                            objectFit: "cover",
                            borderRadius: 8,
                          }}
                        />
                      </div>

                      <div
                        style={{
                          display: "flex",
                          gap: 8,
                          marginTop: 8,
                          overflowX: "auto",
                          paddingBottom: 6,
                        }}
                      >
                        {photos.map((src, i) => (
                          <Image
                            key={i}
                            src={src}
                            width={80}
                            height={60}
                            preview={true}
                            style={{
                              objectFit: "cover",
                              borderRadius: 4,
                              cursor: "pointer",
                            }}
                          />
                        ))}
                      </div>
                    </Image.PreviewGroup>
                  </div>
                ) : (
                  <div
                    style={{
                      height: 420,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <div>No photos available</div>
                  </div>
                )}
              </Card>
              <Badge.Ribbon text={tag?.text} color={tag?.color}>
                <Card style={{ marginTop: 16 }}>
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                  >
                    <div>
                      <div style={{ fontSize: 20, fontWeight: 700 }}>
                        {data?.title}
                      </div>
                      <div style={{ color: "#666", marginTop: 6 }}>
                        {data?.SKU}
                      </div>

                      <div
                        style={{
                          display: "flex",
                          gap: 12,
                          marginTop: 10,
                          alignItems: "center",
                          flexWrap: "wrap",
                        }}
                      >
                        {data?.group_size != null && (
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: 6,
                              color: "#333",
                            }}
                            aria-label="group-size"
                          >
                            <UserOutlined style={{ fontSize: 16 }} />
                            <span style={{ fontSize: 14 }}>
                              {data.group_size}{" "}
                              {String(data.group_size) === "1"
                                ? "person"
                                : "persons"}
                            </span>
                          </div>
                        )}

                        {data?.dates && (
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: 6,
                              color: "#333",
                            }}
                            aria-label="date"
                          >
                            <CalendarOutlined style={{ fontSize: 16 }} />
                            <span style={{ fontSize: 14 }}>{data.dates}</span>
                          </div>
                        )}

                        {data?.place && (
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: 6,
                              color: "#333",
                            }}
                            aria-label="place"
                          >
                            <EnvironmentOutlined style={{ fontSize: 16 }} />
                            <span style={{ fontSize: 14 }}>{data.place}</span>
                          </div>
                        )}
                      </div>
                    </div>

                    <div>
                      {data?.price ? (
                        <Tag
                          color="green"
                          style={{ fontSize: 16, padding: "6px 12px" }}
                        >
                          {data.currency
                            ? `${data.currency} ${data.price}`
                            : `${data.price}`}
                        </Tag>
                      ) : (
                        <Tag color="blue">Price on request</Tag>
                      )}
                    </div>
                  </div>

                  <Divider />

                  {accessToken ? (
                    <Space>
                      <Button
                        type="primary"
                        onClick={() => handleActionClick("quote")}
                        loading={loading || quoteMutation.isLoading}
                        disabled={tag?.text === "Sold out"}
                        style={{ marginLeft: 8 }}
                      >
                        Get Quote
                      </Button>
                    </Space>
                  ) : (
                    <Button
                      type="primary"
                      loading={loading || quoteMutation.isLoading}
                      onClick={() => handleActionClick("quote")}
                      style={{ marginLeft: 8 }}
                    >
                      Sign In To Get Quote
                    </Button>
                  )}
                  {data?.itinerary?.length > 0 && (
                    <DownloadItineraryButton data={data} type="btn" />
                  )}
                </Card>
              </Badge.Ribbon>
            </Col>

            <Col xs={24} md={14} lg={16} className="service-detail-right">
              {data?.description && (
                <>
                  <Title>{data.title}</Title>
                  <Paragraph style={{ whiteSpace: "pre-wrap" }}>
                    {data.description}
                  </Paragraph>
                </>
              )}
              {renderPromotions(data?.promotions)}
              {renderItinerary(data?.itinerary, data?.transfers || [])}

              {renderBulletList("Included", data?.included)}
              {renderBulletList("Excluded", data?.excluded)}
              {renderBulletList("Pre-requirements", data?.pre_requerments)}
              {renderStayOptions(data?.stay_options)}

              {renderAddons(data?.addons)}
              {renderPaymentOptions(data?.payment_options)}
              {renderBulletList(
                "Terms & Conditions",
                data?.terms_and_conditions,
              )}
              {renderBulletList(
                "Cancellation Policy",
                data?.cancellation_policy,
              )}
              {renderTags(data?.tags)}
            </Col>
          </Row>

          <Modal
            title={"Request a Quote"}
            open={modalOpen}
            onCancel={() => setModalOpen(false)}
            onOk={handleConfirm}
            okText={"Send Quote Request"}
            confirmLoading={loading || quoteMutation.isLoading}
            okButtonProps={{
              disabled: !accessToken,
            }}
            width={380}
            styles={{ body: { paddingTop: 10, paddingBottom: 10 } }}
          >
            <Space direction="vertical" style={{ width: "100%" }} size="small">
              <Alert
                type="info"
                showIcon
                message="We respect your privacy"
                style={{
                  fontSize: 11,
                  borderRadius: 6,
                  padding: "6px 10px",
                }}
                description={
                  <div style={{ fontSize: 12, lineHeight: 1.3 }}>
                    • No spam calls or emails.
                    <br />• Stored securely with Google encryption.
                  </div>
                }
              />

              <Text type="secondary" style={{ fontSize: 11, marginTop: -6 }}>
                By continuing, you agree to be contacted by our team for this
                enquiry. You can ask us to delete your data anytime.
              </Text>
            </Space>
            {userDetails?.role === "admin" && (
              <Space direction="vertical" style={{ width: "100%" }}>
                <Text strong>Admin Lookup (optional)</Text>

                <Input.Search
                  placeholder="Enter user email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  onSearch={handleEmailSearch}
                  loading={emailLookupLoading}
                  enterButton
                />

                <Input
                  placeholder="Phone number"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                />

                {emailLookupError && (
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    {emailLookupError}
                  </Text>
                )}

                <Divider style={{ margin: "8px 0" }} />
              </Space>
            )}
          </Modal>
        </div>
      ) : (
        <div style={{ padding: 16, minHeight: "90vh", paddingTop: "45vh" }}>
          <Row justify="center">
            {isLoading && !data ? <Spin size="large" /> : <p>no data found</p>}
          </Row>
        </div>
      )}
    </>
  );
}
